import React, { useState, useEffect } from "react";
import styled from "styled-components";
import Navbar from "../../components/layout/Navbar/Navbar";
import Sidebar from "../../components/layout/Sidebar/Sidebar";
import UsersPendingHeader from "../../components/features/users/UsersPendentesHeader";
import { getCargoIdSidebar } from "../../constants/sidebarConfig";
import apiService from "../../services/api";
import { applyUserFilters } from "../../utils/userFilters";
import { usePopup } from "../../components/common/Popup";
import "../../components/features/users/user-table.css";
import visibilityIcon from "../../assets/visibility.png";
import doNotIcon from "../../assets/do not.png";
import yesWhiteIcon from "../../assets/yes white.png";
import ProfileUserInfo from "../../components/Profile/ProfileUserInfo";

const NAVBAR_HEIGHT = 65;
const SIDEBAR_WIDTH = 300;

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #f9f9f9;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex: 1;
  margin-top: 60px;
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: ${SIDEBAR_WIDTH}px;
  overflow-y: auto;
  height: calc(100vh - ${NAVBAR_HEIGHT}px);
`;


const UsersPendentes = () => {
  const popup = usePopup();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [user, setUser] = useState(null);
  const [sidebarItems, setSidebarItems] = useState([]);
  const [loading, setLoading] = useState({});
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileModalUser, setProfileModalUser] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [filters, setFilters] = useState({
    cargoId: null,
    sortBy: 'nome',
    order: 'asc',
    searchTerm: ''
  });
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem('token');

  const openUserProfile = async (rowUser) => {
    setProfileModalOpen(true);
    setProfileModalUser(null);
    setProfileLoading(true);
    try {
      const full = await apiService.getUsuarioById(rowUser.id);
      setProfileModalUser({ ...full, status: rowUser.status });
    } catch (err) {
      console.error(err);
      setProfileModalOpen(false);
      await popup.alert({
        icon: 'warning',
        title: 'Erro',
        message: 'Não foi possível carregar o perfil do usuário.',
      });
    } finally {
      setProfileLoading(false);
    }
  };

  const closeUserProfile = () => {
    setProfileModalOpen(false);
    setProfileModalUser(null);
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${baseUrl}/users`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Erro ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      const pendingUsers = data.filter(user => user.status === 'PENDENTE' || user.status === 'RECUSADO');

      setUsers(pendingUsers);

      const filtered = applyUserFilters(pendingUsers, filters);
      setFilteredUsers(filtered);
    } catch (err) {
      console.error('Erro na requisição:', err);
    }
  };

  const handleApprove = async (userId) => {
    const user = users.find(user => user.id === userId);
    if (!user) {
      await popup.alert({
        icon: 'warning',
        message: 'Usuário não encontrado.',
      });
      return;
    }
    const isCoordenadorInstituicao = user.cargoId === 2;
    const isCoordenadorCurso = user.cargoId === 3;
    const confirmMessage = isCoordenadorInstituicao
      ? 'Deseja aprovar este usuário? Isso irá ativar sua conta e criar a instituição.'
      : isCoordenadorCurso
        ? 'Deseja aprovar este usuário? Isso irá ativar sua conta e vinculá-lo a instituição de ensino correspondentes.'
        : 'Deseja aprovar este usuário? Isso irá ativar sua conta.';

    const confirmed = await popup.confirm({
      icon: 'info',
      message: confirmMessage,
    });
    if (!confirmed) return;

    setLoading(prev => ({ ...prev, [userId]: 'aprovar' }));

    try {
      if (isCoordenadorInstituicao || isCoordenadorCurso) {
        await apiService.aprovarUsuario(userId);
      } else {
        await apiService.updateUserStatus(userId, { status: 'ATIVO' });
      }

      await popup.alert({
        icon: 'success',
        message: 'Usuário aprovado com sucesso!',
      });
      fetchUsers();
    } catch (error) {
      console.error('Erro ao aprovar usuário:', error);
      await popup.alert({
        icon: 'warning',
        title: 'Erro ao aprovar',
        message: error.message || 'Tente novamente.',
      });
    } finally {
      setLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleReject = async (userId) => {
    const confirmed = await popup.confirm({
      icon: 'warning',
      message: 'Deseja recusar este usuário? Ele poderá ser aprovado posteriormente.',
    });
    if (!confirmed) return;

    setLoading(prev => ({ ...prev, [userId]: 'recusar' }));

    try {
      await apiService.recusarUsuario(userId);
      await popup.alert({
        icon: 'success',
        message: 'Usuário recusado com sucesso!',
      });
      fetchUsers();
    } catch (error) {
      console.error('Erro ao recusar usuário:', error);
      await popup.alert({
        icon: 'warning',
        title: 'Erro ao recusar',
        message: error.message || 'Tente novamente.',
      });
    } finally {
      setLoading(prev => ({ ...prev, [userId]: null }));
    }
  };

  const handleDownload = async (user) => {
    try {
      await apiService.downloadTermoCooperacao(user.id);
    } catch (error) {
      console.error('Erro ao baixar termo:', error);
      await popup.alert({
        icon: 'warning',
        title: 'Erro ao baixar termo',
        message: error.message || 'Tente novamente.',
      });
    }
  };

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userData = await apiService.getCurrentUser();
        setUser(userData);
        const filteredItems = getCargoIdSidebar(userData.cargoId);
        setSidebarItems(filteredItems);
      } catch (err) {
        console.error('Erro ao buscar usuário:', err);
      }
    };

    fetchUser();
    fetchUsers();
  }, []);

  const handleFilterChange = (cargoId) => {
    const newFilters = { ...filters, cargoId: cargoId === 'all' ? null : cargoId };
    setFilters(newFilters);
    const filtered = applyUserFilters(users, newFilters);
    setFilteredUsers(filtered);
  };

  const handleSortChange = ({ sortBy, order }) => {
    const newFilters = { ...filters, sortBy, order };
    setFilters(newFilters);
    const filtered = applyUserFilters(users, newFilters);
    setFilteredUsers(filtered);
  };

  const handleSearchChange = (term) => {
    const newFilters = { ...filters, searchTerm: term };
    setFilters(newFilters);
    const filtered = applyUserFilters(users, newFilters);
    setFilteredUsers(filtered);
  };

  useEffect(() => {
    if (users.length > 0) {
      const filtered = applyUserFilters(users, filters);
      setFilteredUsers(filtered);
    }
  }, [filters, users]);

  return (
    <PageWrapper>
      <Navbar 
        onLogout={apiService.logout}
        showHome={true}
        showNotifications={true}
        showProfile={true}
        showLogout={true}
        user={user}
      />
      <ContentWrapper>
        <Sidebar items={sidebarItems} />
        <MainContent>
          <UsersPendingHeader 
            onFilterChange={handleFilterChange}
            onSortChange={handleSortChange}
            searchValue={filters.searchTerm}
            onSearchChange={handleSearchChange}
          />
          <div className="users-container">
            {filteredUsers.length > 0 || users.length > 0 ? (
              <div className="section">
                <div className="table-header">
                  <h4 className="pending-title">Pedidos de acesso ao sistema</h4>
                </div>
                <div className="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>NOME</th>
                        <th>FUNÇÃO</th>
                        <th data-title="TCT Nº">
                          <span>Nº COOPERAÇÃO TÉCN.(TCT)</span>
                        </th>
                        <th>ARQUIVO</th>
                        <th className="actions-header">AÇÕES</th>
                        <th>STATUS</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map((user) => (
                        <tr key={user.id}>
                          <td>{user.nome}</td>
                          <td>{user.cargo || '-'}</td>
                          <td>{user.instituicaoCadastro?.numeroCooperacao || '-'}</td>
                          <td>
                            {user.cargoId === 2 ? (
                              <button
                                className="btn-download"
                                onClick={() => handleDownload(user)}
                              >
                                Baixar 📎
                              </button>
                            ) : (
                              '-'
                            )}
                          </td>
                          <td className="actions">
                            <button
                              type="button"
                              className="btn-reject"
                              onClick={() => handleReject(user.id)}
                              disabled={loading[user.id]}
                            >
                              {loading[user.id] === 'recusar' ? 'Recusando...' : (
                                <>
                                  Recusar <img src={doNotIcon} alt="Recusar" style={{ width: 16, height: 16, marginLeft: 4 }} />
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              className="btn-approve"
                              onClick={() => handleApprove(user.id)}
                              disabled={loading[user.id]}
                            >
                              {loading[user.id] === 'aprovar' ? 'Aprovando...' : (
                                <>
                                  Aprovar <img src={yesWhiteIcon} alt="Aprovar" style={{ width: 16, height: 16, marginLeft: 4 }} />
                                </>
                              )}
                            </button>
                            <button
                              type="button"
                              className="btn-view-icon"
                              onClick={() => openUserProfile(user)}
                              title="Ver perfil"
                            >
                              <img src={visibilityIcon} alt="Ver" style={{ width: 22, height: 22 }} />
                            </button>
                          </td>
                          <td>
                            {user.cargoId === 2 ? (
                              user.instituicaoCadastro?.status === 'EM_ANALISE' ? (
                                <span className="status-badge status-analysis">
                                  EM ANÁLISE
                                </span>
                              ) : (
                                <span className="status-badge status-rejected">
                                  RECUSADO
                                </span>
                              )
                            ) : (
                              user?.status === 'PENDENTE' ? (
                                <span className="status-badge status-analysis">
                                  {user.status}
                                </span>
                              ) : (
                                <span className="status-badge status-rejected">
                                  RECUSADO
                                </span>
                              )
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="section">
                <div className="table-header">
                  <h4 className="pending-title">Pedidos de acesso ao sistema</h4>
                </div>
                <div className="table-wrapper">
                  <table>
                    <tbody>
                      <tr>
                        <td colSpan="6" className="no-data">
                          {users.length === 0 ? 'Nenhum usuário pendente encontrado' : 'Nenhum usuário encontrado com os filtros aplicados'}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
          {profileModalOpen && (
            <div
              className="modal-overlay"
              role="presentation"
              onClick={closeUserProfile}
            >
              <div
                className="modal-content user-profile-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="user-profile-modal-title-pendentes"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h3 id="user-profile-modal-title-pendentes">Perfil do usuário</h3>
                  <button
                    type="button"
                    className="modal-close"
                    onClick={closeUserProfile}
                    aria-label="Fechar"
                  >
                    ×
                  </button>
                </div>
                <div className="modal-body user-profile-modal-body">
                  {profileLoading && <p>Carregando...</p>}
                  {!profileLoading && profileModalUser && (
                    <ProfileUserInfo
                      key={profileModalUser.id}
                      userData={profileModalUser}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </MainContent>
      </ContentWrapper>
    </PageWrapper>
  );
};

export default UsersPendentes;
